import * as ko from "knockout";
import {ICondition, IRule} from "../../../../index";
import {KOSubscriptionTracker} from "../../../../util/KOSubscriptionTracker";
import {ObjectTypeId} from "../../../../constants";
import {AvailableData} from "./AvailableData";
import {ErrorObject} from "../../../../error/ErrorObject";
import {validateServerDate} from "../../../../util/datetime";
import {nsRuleset} from "../../nsRuleset";
import {nsDynamicVariable} from "../../../dynamic-variable/nsDynamicVariable";
import {IRuleMutable} from "./index";
import {nsRule} from "./nsRule";
import getCondition = nsRuleset.getCondition;
import validateRange = nsRuleset.validateRange;
import validateExpression = nsDynamicVariable.validateExpression;

export class MutableRuleAbstract implements IRuleMutable {
    ObjectTypeId = ObjectTypeId;

    $original!: IRule;
    public readonly argument: KnockoutObservable<IRule['argument'] | null> = ko.observable(null);
    public readonly argumentoperator: KnockoutObservable<IRule['argumentoperator'] | null> = ko.observable(null);
    public readonly attribute: KnockoutObservable<IRule['attribute'] | null> = ko.observable(null);
    public readonly booleanoperator: KnockoutObservable<IRule['booleanoperator'] | null> = ko.observable(null);
    public readonly order: KnockoutObservable<IRule['order'] | null> = ko.observable(null);

    private readonly errArgument: ErrorObject = new ErrorObject({name: 'argument'});
    private readonly errArgumentOperator: ErrorObject = new ErrorObject({name: 'argumentOperator'});
    private readonly errAttribute: ErrorObject = new ErrorObject({name: 'attribute'});

    kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private errorObjects!: ErrorObject[];

    constructor(public availableData: AvailableData) {
    }

    public initErrorTracking() {
        if (!this.errorObjects) {
            this.errAttribute.addValidator({
                fn: () => this.validateAttribute(),
                message: 'invalid attribute',
            })
            this.errArgumentOperator.addValidator({
                fn: () => this.validateArgumentOperator(),
                message: 'invalid condition',
            })
            this.errArgument.addValidator({
                fn: () => this.validateArgument(),
                message: 'invalid argument',
            })

            this.errorObjects = [
                this.errAttribute,
                this.errArgumentOperator,
                this.errArgument,
            ]
        }
        this.errorObjects.forEach(errObj => this.availableData.errManagerRules.addErrorObject(errObj));
    }

    private validateAttribute(): boolean {
        const {name, type} = this.computedArgumentOperator();
        const isMandatory: boolean = (() => {
            switch (type) {
                case "ruleset":
                    return false;
            }
            switch (name) {
                case 'Er medlem av':
                case 'Er medlem av en av':
                case 'Har ikke medlemsskap':
                case 'Har medlemsskap':
                    return false;
            }
            return true;
        })();
        return !isMandatory || !!this.attribute();
    }

    private validateArgumentOperator(): boolean {
        const {name} = this.computedArgumentOperator();
        return !!this.argumentoperator() && !!name;
    }

    private validateArgument(): boolean {
        if (!this.argumentoperator()) {
            return true;
        }
        const {type} = this.computedArgumentOperator();
        const arg = this.argument() as string;
        switch (type) {
            case "bool":
                return arg === '';
            case "datetime":
                return (validateRange(arg) || validateServerDate(arg) || validateExpression(arg)) ?? false;
            case "int":
                // The "int" condition type covers all numeric arguments (>, <, En/Alle
                // større/mindre enn, Antall). Decimals are accepted with '.' as the
                // decimal separator so that values like position percentages match
                // server-side (server parses with InvariantCulture). Backwards
                // compatible: pure integers still pass.
                return !!arg && (/^[0-9]+(\.[0-9]+)?$/.test(arg) || (validateExpression(arg) ?? false))
        }
        return !!arg;
    }

    private computedArgumentOperator = ko.pureComputed((): ICondition => {
        return getCondition(this.argumentoperator() ?? '', this.availableData.conditions)
            ?? {name: '', type: 'string'} as unknown as ICondition;
    })

    toStatic(): IRule {
        return {
            argument: this.argument() ?? '',
            argumentoperator: (this.argumentoperator() ?? '') as IRule['argumentoperator'],
            attribute: this.attribute() || "",
            booleanoperator: this.booleanoperator() || "",
            id: this.$original.id,
            order: this.order() ?? 0,
            rulesetid: this.$original.rulesetid,
            rulesetpath: '',
        };
    }

    getArgumentType(): ICondition['type'] {
        return this.availableData.getArgumentType(this.argumentoperator() ?? '') ?? 'string';
    }

    public doDialogPickObjectAttribute() {
        nsRule.doDialogPickObjectAttributeValue((val: any) => {
            this.argument(val);
        }, this);
    }

    dispose() {
        this.kosub.unsuscribeAll();
        this.errorObjects?.forEach(errObj => this.availableData.errManagerRules.removeErrorObject(errObj));
    }
}
